import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Sheet,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { CongregationRole } from '../../types/congregation';

dayjs.extend(relativeTime);

export interface ActivityTimelineItem {
  id: number | string;
  title: string;
  description?: string | null;
  timestamp: string;
  actorName?: string | null;
  actorRole?: CongregationRole | null;
  contextLabel?: string | null;
}

interface ActivityTimelineProps {
  items: ActivityTimelineItem[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyMessage?: string;
}

const actorRoleLabels: Record<CongregationRole, string> = {
  ADMIN: 'Admin',
  DEVOTEE: 'Devotee',
  MENTOR: 'Mentor',
  FACILITATOR: 'Facilitator',
};

const timelineDotSx = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  bgcolor: 'primary.500',
  boxShadow: '0 0 0 2px var(--joy-palette-background-level1)',
};

export function ActivityTimeline({
  items,
  loading = false,
  error,
  onRetry,
  emptyMessage = 'No activity recorded yet.',
}: ActivityTimelineProps) {
  if (loading) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
        <CircularProgress size="md" aria-label="Loading activity" />
        <Typography level="body-sm" color="neutral">
          Loading activity…
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Sheet
        variant="soft"
        color="danger"
        sx={{
          borderRadius: 'lg',
          py: 3,
          px: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography level="body-sm">{error}</Typography>
        {onRetry ? (
          <Button size="sm" variant="solid" color="danger" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
      </Sheet>
    );
  }

  if (!items.length) {
    return (
      <Sheet
        variant="soft"
        color="neutral"
        sx={{ borderRadius: 'lg', py: 3, px: 2.5, textAlign: 'center' }}
      >
        <Typography level="body-sm">{emptyMessage}</Typography>
      </Sheet>
    );
  }

  return (
    <Stack spacing={2} divider={<Divider />}> 
      {items.map((item) => {
        const absolute = dayjs(item.timestamp).format('DD MMM YYYY HH:mm');
        const relative = dayjs(item.timestamp).fromNow();
        return (
          <Stack key={item.id} direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ pt: 0.75 }}>
              <Box sx={timelineDotSx} />
            </Box>
            <Stack spacing={0.75} flex={1}>
              <Typography level="title-sm">{item.title}</Typography>
              {item.description ? (
                <Typography level="body-sm" color="neutral">
                  {item.description}
                </Typography>
              ) : null}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
              >
                <Tooltip title={absolute}>
                  <Typography level="body-xs" color="neutral">
                    {relative}
                  </Typography>
                </Tooltip>
                {item.contextLabel ? (
                  <Chip size="sm" variant="soft" color="neutral">
                    {item.contextLabel}
                  </Chip>
                ) : null}
                {item.actorName ? (
                  <Chip size="sm" variant="soft" color="primary">
                    {item.actorRole
                      ? `${item.actorName} · ${actorRoleLabels[item.actorRole] ?? item.actorRole}`
                      : item.actorName}
                  </Chip>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}
